/**
 * L'artefact est unique ; l'hôte décide de ce qu'il montre.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * POURQUOI L'HÔTE, ET PAS UN CHEMIN `/console`
 * ────────────────────────────────────────────────────────────────────────────
 *
 * ANTICIPATION-MULTITENANT §3.6 a tranché : la console vit sur
 * `admin.ticket.ilygo.ch`, pas sur `/console` d'un théâtre. Trois
 * conséquences concrètes, que ce fichier de douze lignes achète :
 *
 *  · **les cookies sont cloisonnés par le navigateur.** `tb_console` est posé
 *    par une origine que ne partage aucun théâtre : il ne peut pas voyager vers
 *    `baladin.ticket.ilygo.ch`, et réciproquement `tb_staff` n'arrive jamais à
 *    la console. Aucune ligne de code ne tient cette garantie — l'origine la
 *    tient ;
 *  · **le back-office ne bouge pas d'un caractère.** `CHEMINS`, `BASE_API`, le
 *    routeur, les 20 `goto()` des tests E2E : rien ne change ;
 *  · **le backend applique la même frontière.** Les routes de console sont de
 *    portée `console` et répondent 404 depuis l'adresse d'un théâtre
 *    (`plateforme/hooks.ts`). Le frontend et le backend décident donc la même
 *    chose, à partir du même signal.
 *
 * En développement, `admin.localhost:5173` résout sans DNS ni certificat, sur
 * macOS comme sur Linux — c'est ce qui rend la console testable en local sans
 * toucher à `/etc/hosts`.
 */

/** Étiquette de la console, alignée sur `CONSOLE_SOUS_DOMAINE` du backend. */
export const SOUS_DOMAINE_CONSOLE = 'admin'

/**
 * `true` si la page est servie depuis l'hôte de la console.
 *
 * ★ La comparaison porte sur la **première étiquette** du nom d'hôte, jamais
 *   sur `includes('console')` : un théâtre nommé `console-du-leman` ne doit pas
 *   se retrouver à afficher la console d'exploitation. Le sous-domaine est un
 *   identifiant, pas une sous-chaîne.
 */
export function estHoteConsole(hote: string = globalThis.location?.hostname ?? ''): boolean {
  const etiquette = hote.toLowerCase().split('.')[0] ?? ''
  return etiquette === SOUS_DOMAINE_CONSOLE
}

/**
 * Le domaine sous lequel naissent les théâtres — `TENANTS_DOMAINE` côté
 * backend — **déduit de l'hôte servi**, jamais écrit en dur.
 *
 * ★★ POURQUOI CE N'EST PAS UN DÉTAIL. L'écran de provisionnement annonçait
 *    `<slug>.ticket.ilygo.ch`, en dur. Or les deux environnements ont des
 *    hôtes **asymétriques et assumés** : `tickets.uat.ilygo.ch` au pluriel en
 *    recette, `ticket.ilygo.ch` au singulier en production. Depuis la console
 *    de recette, l'exploitant lisait donc — et recopiait au client — une
 *    adresse de production qui n'existe pas pour ce théâtre. Ni le formulaire
 *    ni le courriel ne pouvaient le rattraper : c'est une chaîne, elle ne se
 *    trompe jamais toute seule.
 *
 *    La console est servie depuis `admin.<domaine>` : le domaine des
 *    théâtres, c'est l'hôte moins sa première étiquette. Le calcul suit donc
 *    l'environnement sans qu'on ait à le lui dire.
 *
 * Replis prudents : un hôte sans point (`localhost`) ou vide est rendu tel
 * quel plutôt que réduit à une chaîne vide — mieux vaut une adresse locale
 * lisible qu'un « .ch » orphelin.
 */
export function domaineDesTheatres(hote: string = globalThis.location?.hostname ?? ''): string {
  const nom = hote.toLowerCase().trim()
  if (nom === '') return ''
  const etiquettes = nom.split('.')
  if (etiquettes.length < 2) return nom
  return etiquettes[0] === SOUS_DOMAINE_CONSOLE ? etiquettes.slice(1).join('.') : nom
}
